import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import Seo from "@/components/Seo";
import { orgSchema, breadcrumb } from "@/lib/seo";
import { BLOG_POSTS } from "@/data/blog";
import { ArrowRight, Clock } from "@phosphor-icons/react";

export default function Blog() {
  const { t, lang } = useI18n();
  const [featured, ...rest] = BLOG_POSTS;
  const fmt = (d) => new Date(d).toLocaleDateString(lang === "de" ? "de-CH" : "en-GB", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="blog-page">
      <Seo
        title={t("seo_blog_title")}
        description={t("blog_sub") || t("seo_blog_desc")}
        path="/blog"
        jsonLd={[orgSchema(), breadcrumb([{ name: "Home", path: "/" }, { name: t("nav_blog"), path: "/blog" }])]}
      />
      <p className="font-body text-xs uppercase tracking-[0.22em] text-[#EF5A1E] font-semibold mb-4">{t("blog_kicker")}</p>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-[1.05] max-w-3xl">{t("blog_title")}</h1>
      <p className="font-body text-[#5A607A] mt-5 text-lg max-w-2xl">{t("blog_sub")}</p>

      {/* Featured — editorial hero */}
      <Link to={`/blog/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`} className="group relative block w-full aspect-[16/10] md:aspect-[2.4/1] rounded-3xl overflow-hidden mt-12">
        <img src={featured.image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1533] via-[#0B1533]/45 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10 md:p-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-[#EF5A1E]">{featured.category[lang]}</span>
            <span className="font-body text-xs text-white/60 flex items-center gap-1"><Clock size={13} /> {featured.readTime} {t("read_time")}</span>
          </div>
          <h2 className="font-heading font-bold tracking-tight text-2xl sm:text-4xl md:text-5xl text-white leading-tight max-w-3xl group-hover:text-[#EF5A1E] transition-colors duration-300">{featured.title[lang]}</h2>
          <p className="font-body text-white/70 mt-4 leading-relaxed max-w-2xl hidden sm:block">{featured.excerpt[lang]}</p>
          <span className="inline-flex items-center gap-2 mt-6 font-body font-semibold text-white">{t("read_more")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" /></span>
        </div>
      </Link>

      {/* Grid — editorial cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 mt-16">
        {rest.map((p, i) => (
          <motion.div key={p.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
            <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex flex-col h-full">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-5">
                <img src={p.image} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              </div>
              <div className="flex items-center gap-3 mb-2">
                <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#EF5A1E]">{p.category[lang] || p.category.en}</span>
                <span className="font-body text-xs text-[#9CA0B0]">{fmt(p.date)}</span>
              </div>
              <h3 className="font-heading font-semibold text-xl sm:text-2xl tracking-tight text-[#0B1533] leading-snug group-hover:text-[#EF5A1E] transition-colors duration-200">{p.title[lang] || p.title.en}</h3>
              <p className="font-body text-sm text-[#5A607A] mt-2.5 leading-relaxed line-clamp-2">{p.excerpt[lang]}</p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
